/**
 * Lista de profesionales del policlínico (médicos, odontólogos, enfermeros,
 * obstetras, psicólogos, tecnólogos médicos, nutricionistas, QFs, etc.).
 */
import { useState, type FormEvent } from "react";
import { TelefonoInput } from "../components/TelefonoInput";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Stethoscope, UserCheck } from "lucide-react";
import {
  type EspecialidadMini,
  type ProfesionalCreate,
  type TipoColegio,
  createProfesional,
  listEspecialidades,
  listProfesionales,
} from "../api/profesionales";
import { reniecLookup } from "../api/pacientes";
import { listSedes } from "../api/tenants";
import { useBase } from "../auth/TenantBrandingProvider";
import { ApiError } from "../api/client";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Field,
  Input,
} from "../components/ui";

const TIPOS_COLEGIO: { value: TipoColegio; label: string }[] = [
  { value: "CMP", label: "CMP — Colegio Médico del Perú" },
  { value: "COP", label: "COP — Colegio Odontológico del Perú" },
  { value: "CQFP", label: "CQFP — Colegio Químico Farmacéutico del Perú" },
  { value: "CEP", label: "CEP — Colegio de Enfermeros del Perú" },
  { value: "CObP", label: "CObP — Colegio de Obstetras del Perú" },
  { value: "CTMP", label: "CTMP — Colegio Tecnólogo Médico del Perú" },
  { value: "CPP", label: "CPP — Colegio de Psicólogos del Perú" },
  { value: "CNP", label: "CNP — Colegio de Nutricionistas del Perú" },
  { value: "CBP", label: "CBP — Colegio de Biólogos del Perú" },
];

export function ProfesionalesListPage() {
  const base = useBase();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["profesionales", search],
    queryFn: () => listProfesionales(search || undefined),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Profesionales</h1>
          <p className="text-sm text-slate-500 mt-1">
            Personal de salud del policlínico (médicos, enfermeros, obstetras, psicólogos, tecnólogos médicos, nutricionistas, y más), con su colegiatura y especialidades.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="w-4 h-4" />
          Nuevo profesional
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por DNI, nombre o colegiatura…"
              className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-slate-400"
            />
            {data && (
              <span className="text-xs text-slate-500">
                {data.length} {data.length === 1 ? "profesional" : "profesionales"}
              </span>
            )}
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading && (
            <div className="px-6 py-12 text-center text-sm text-slate-400">Cargando…</div>
          )}
          {error && (
            <div className="px-6 py-4">
              <Alert>No se pudo cargar la lista de profesionales.</Alert>
            </div>
          )}
          {data && data.length === 0 && !isLoading && (
            <div className="px-6 py-12 text-center">
              <Stethoscope className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">
                {search
                  ? `Sin resultados para "${search}".`
                  : "Aún no hay profesionales registrados."}
              </p>
              {!search && (
                <button
                  onClick={() => setCreateOpen(true)}
                  className="mt-3 text-sm text-[color:var(--color-link)] hover:text-[color:var(--color-link)] font-medium"
                >
                  Registrar el primero
                </button>
              )}
            </div>
          )}
          {data && data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <Th>Colegiatura</Th>
                    <Th>Nombre completo</Th>
                    <Th>Especialidades</Th>
                    <Th>Sedes</Th>
                    <Th>Firma digital</Th>
                    <Th>Estado</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-6 py-3 font-mono text-xs">
                        <Badge>{p.tipo_colegio}</Badge>
                        <span className="ml-1 text-slate-700">{p.colegiatura}</span>
                      </td>
                      <td className="px-6 py-3">
                        <Link
                          to={`${base}/profesionales/${p.id}`}
                          className="text-[color:var(--color-link)] hover:text-[color:var(--color-link)] font-medium"
                        >
                          {p.nombre_completo}
                        </Link>
                        <div className="text-xs text-slate-400 font-mono">
                          {p.tipo_documento} {p.num_documento}
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex flex-wrap gap-1">
                          {p.especialidades.length === 0 && (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                          {p.especialidades.map((e) => (
                            <Badge key={e.id}>{e.nombre}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex flex-wrap gap-1">
                          {p.sedes.length === 0 && (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                          {p.sedes.map((s) => (
                            <Badge key={s.id}>{s.nombre}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        {p.firma_digital_activa ? (
                          <Badge variant="success">Activa</Badge>
                        ) : (
                          <span className="text-xs text-slate-400">No configurada</span>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        <Badge variant={p.activo ? "success" : "danger"}>
                          {p.activo ? "Activo" : "Inactivo"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <CreateProfesionalDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
      {children}
    </th>
  );
}

// ---------- Create dialog ----------

export function CreateProfesionalDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const base = useBase();
  const queryClient = useQueryClient();

  const { data: especialidades = [] } = useQuery({
    queryKey: ["especialidades"],
    queryFn: listEspecialidades,
    staleTime: 5 * 60_000,
    enabled: open,
  });

  // `/policlinicos/{id}/sedes` es de super-admin: pedirlo como admin_tenant
  // devolvía 403 y react-query dejaba la lista vacía, así que el formulario
  // decía "no hay sedes" cuando sí las había. Para un usuario del tenant va
  // `/sedes`, que su rol sí puede leer.
  const {
    data: sedes = [],
    isLoading: cargandoSedes,
    isError: errorSedes,
  } = useQuery({
    // Una sola clave: las dos ramas llamaban a `listSedes()` idéntico y
    // diferenciaban la caché por un `tenant_id` que el servidor ya no manda.
    queryKey: ["sedes"],
    queryFn: () =>
      listSedes(),
    staleTime: 5 * 60_000,
    enabled: open,
  });

  const [form, setForm] = useState<ProfesionalCreate>({
    tipo_documento: "DNI",
    num_documento: "",
    nombres: "",
    ap_paterno: "",
    ap_materno: "",
    fecha_nacimiento: null,
    sexo: null,
    email: "",
    telefono: "",
    tipo_colegio: "CMP",
    colegiatura: "",
    rne: "",
    especialidad_codigos: [],
    especialidad_principal: null,
    sede_ids: [],
    sede_principal_id: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [reniecMsg, setReniecMsg] = useState<string | null>(null);
  const [reniecLoading, setReniecLoading] = useState(false);

  const mutation = useMutation({
    mutationFn: (payload: ProfesionalCreate) => createProfesional(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profesionales"] });
      onClose();
      // Reset
      setForm({
        tipo_documento: "DNI",
        num_documento: "",
        nombres: "",
        ap_paterno: "",
        ap_materno: "",
        fecha_nacimiento: null,
        sexo: null,
        email: "",
        telefono: "",
        tipo_colegio: "CMP",
        colegiatura: "",
        rne: "",
        especialidad_codigos: [],
        especialidad_principal: null,
        sede_ids: [],
        sede_principal_id: null,
      });
      setError(null);
      setReniecMsg(null);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        const detail = (err.payload as { detail?: string })?.detail;
        setError(detail ?? "Ya existe un profesional con esos datos.");
      } else if (err instanceof ApiError && err.status === 400) {
        const detail = (err.payload as { detail?: string })?.detail;
        setError(detail ?? "Datos inválidos.");
      } else if (err instanceof ApiError && err.status === 422) {
        const payload = err.payload as { detail?: unknown } | null;
        const detail = payload?.detail;
        if (Array.isArray(detail) && detail.length > 0) {
          const first = detail[0] as { loc?: unknown[]; msg?: string };
          const field = Array.isArray(first.loc)
            ? String(first.loc[first.loc.length - 1] ?? "campo")
            : "campo";
          setError(`Error en "${field}": ${first.msg ?? "valor inválido"}`);
        } else {
          setError("Datos inválidos. Revisa los campos.");
        }
      } else {
        setError("Error al crear. Intenta de nuevo.");
      }
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      ...form,
      ap_materno: form.ap_materno || undefined,
      email: form.email || undefined,
      telefono: form.telefono || undefined,
      rne: form.rne || undefined,
      especialidad_principal: form.especialidad_principal || undefined,
    });
  }

  async function handleReniec() {
    if (form.tipo_documento !== "DNI" || form.num_documento.length !== 8) {
      setReniecMsg("Necesitas un DNI de 8 dígitos.");
      return;
    }
    setReniecLoading(true);
    setReniecMsg(null);
    try {
      const r = await reniecLookup(form.num_documento);
      setForm({
        ...form,
        nombres: r.nombres,
        ap_paterno: r.ap_paterno,
        ap_materno: r.ap_materno,
        sexo: r.sexo,
        fecha_nacimiento: r.fecha_nacimiento,
      });
      setReniecMsg(r.fuente === "MOCK" ? "Datos de prueba (RENIEC mock)." : "Datos consultados a RENIEC.");
    } catch {
      setReniecMsg("No se pudo consultar RENIEC.");
    } finally {
      setReniecLoading(false);
    }
  }

  // Especialidades que corresponden al colegio/profesión seleccionado.
  const especialidadesDelColegio = especialidades.filter((e) =>
    (e.profesiones ?? []).includes(form.tipo_colegio),
  );

  // Al cambiar de colegio, descarta las especialidades ya elegidas que no
  // pertenecen a la nueva profesión (mantiene los datos consistentes).
  function handleTipoColegioChange(tipo_colegio: TipoColegio) {
    const validos = new Set(
      especialidades
        .filter((e) => (e.profesiones ?? []).includes(tipo_colegio))
        .map((e) => e.codigo),
    );
    setForm({
      ...form,
      tipo_colegio,
      especialidad_codigos: form.especialidad_codigos.filter((c) => validos.has(c)),
      especialidad_principal:
        form.especialidad_principal && validos.has(form.especialidad_principal)
          ? form.especialidad_principal
          : null,
    });
  }

  function toggleEspecialidad(codigo: string) {
    const list = form.especialidad_codigos;
    if (list.includes(codigo)) {
      const newList = list.filter((c) => c !== codigo);
      setForm({
        ...form,
        especialidad_codigos: newList,
        especialidad_principal:
          form.especialidad_principal === codigo ? null : form.especialidad_principal,
      });
    } else {
      setForm({ ...form, especialidad_codigos: [...list, codigo] });
    }
  }

  function toggleSede(sedeId: number) {
    const list = form.sede_ids;
    if (list.includes(sedeId)) {
      const newList = list.filter((s) => s !== sedeId);
      setForm({
        ...form,
        sede_ids: newList,
        sede_principal_id:
          form.sede_principal_id === sedeId ? null : form.sede_principal_id,
      });
    } else {
      setForm({ ...form, sede_ids: [...list, sedeId] });
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Nuevo profesional"
      description="Registra un profesional de salud (médico, enfermero, obstetra, psicólogo, tecnólogo médico, nutricionista, etc.) con su colegiatura y especialidades."
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        {/* Documento + RENIEC */}
        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">Datos personales</p>
          <div className="grid grid-cols-[140px_1fr_auto] gap-3">
            <Field label="Tipo doc." htmlFor="p_tipo">
              <select
                id="p_tipo"
                value={form.tipo_documento}
                onChange={(e) => setForm({ ...form, tipo_documento: e.target.value as any })}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="DNI">DNI</option>
                <option value="CE">CE</option>
                <option value="PAS">Pasaporte</option>
              </select>
            </Field>
            <Field label="Número" htmlFor="p_num">
              <Input
                id="p_num"
                value={form.num_documento}
                onChange={(e) => setForm({ ...form, num_documento: e.target.value })}
                maxLength={20}
                required
                className="font-mono"
              />
            </Field>
            <div className="flex items-end">
              <button
                type="button"
                onClick={handleReniec}
                disabled={reniecLoading || form.tipo_documento !== "DNI"}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                <UserCheck className="w-3.5 h-3.5" />
                {reniecLoading ? "Consultando…" : "RENIEC"}
              </button>
            </div>
          </div>
          {reniecMsg && <p className="text-xs text-slate-500 mt-1">{reniecMsg}</p>}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Nombres" htmlFor="p_nombres">
            <Input
              id="p_nombres"
              value={form.nombres}
              onChange={(e) => setForm({ ...form, nombres: e.target.value })}
              required
            />
          </Field>
          <Field label="Ap. paterno" htmlFor="p_apat">
            <Input
              id="p_apat"
              value={form.ap_paterno}
              onChange={(e) => setForm({ ...form, ap_paterno: e.target.value })}
              required
            />
          </Field>
          <Field label="Ap. materno" htmlFor="p_amat">
            <Input
              id="p_amat"
              value={form.ap_materno ?? ""}
              onChange={(e) => setForm({ ...form, ap_materno: e.target.value })}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Teléfono" htmlFor="p_tel">
            <TelefonoInput
              id="p_tel"
              value={form.telefono}
              onChange={(v) => setForm({ ...form, telefono: v })}
            />
          </Field>
          <Field label="Email" htmlFor="p_email">
            <Input
              id="p_email"
              type="email"
              value={form.email ?? ""}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
        </div>

        {/* Colegiatura */}
        <div className="pt-2 border-t border-slate-100">
          <p className="text-sm font-medium text-slate-700 mb-2">Colegiatura</p>
          <div className="grid grid-cols-[1fr_140px] gap-3">
            <Field label="Colegio profesional" htmlFor="p_col">
              <select
                id="p_col"
                value={form.tipo_colegio}
                onChange={(e) => handleTipoColegioChange(e.target.value as TipoColegio)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                {TIPOS_COLEGIO.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="N°" htmlFor="p_cn">
              <Input
                id="p_cn"
                value={form.colegiatura}
                onChange={(e) => setForm({ ...form, colegiatura: e.target.value })}
                maxLength={20}
                required
                className="font-mono"
              />
            </Field>
          </div>
          <Field label="RNE (opcional)" htmlFor="p_rne" hint="Registro Nacional de Especialistas. Aplica solo a médicos especialistas.">
            <Input
              id="p_rne"
              value={form.rne ?? ""}
              onChange={(e) => setForm({ ...form, rne: e.target.value })}
              maxLength={20}
              className="font-mono"
            />
          </Field>
        </div>

        {/* Especialidades (filtradas por el colegio profesional elegido) */}
        <div className="pt-2 border-t border-slate-100">
          <p className="mb-0.5 text-sm font-medium text-slate-700">Especialidades</p>
          {/* Doble filtro invisible: de todo el catálogo solo se ofrecen unas
              pocas, y de esas solo las que el colegio elegido puede ejercer.
              Sin decirlo, la lista corta parece un error del sistema. */}
          <p className="mb-2 text-xs text-slate-500">
            {especialidadesDelColegio.length} de las {especialidades.length} que ofrece
            el policlínico corresponden a {form.tipo_colegio}.{" "}
            <Link
              to={`${base}/especialidades`}
              className="font-medium text-[color:var(--color-link)] hover:text-[color:var(--color-link)] hover:underline"
            >
              Ofrecer más especialidades
            </Link>
          </p>
          {especialidades.length === 0 ? (
            <p className="text-xs text-slate-500">Cargando catálogo…</p>
          ) : especialidadesDelColegio.length === 0 ? (
            <Alert variant="info">
              Ninguna de las especialidades que ofrece el policlínico la ejerce un
              profesional de {form.tipo_colegio}. Actívale una en{" "}
              <Link
                to={`${base}/especialidades`}
                className="font-medium underline"
              >
                Especialidades
              </Link>{" "}
              antes de registrarlo.
            </Alert>
          ) : (
            <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2">
              {especialidadesDelColegio.map((e: EspecialidadMini) => {
                const checked = form.especialidad_codigos.includes(e.codigo);
                const isPrincipal = form.especialidad_principal === e.codigo;
                return (
                  <label
                    key={e.id}
                    className={`flex items-center gap-2 text-sm cursor-pointer rounded p-1.5 hover:bg-slate-50 ${
                      checked ? "bg-[color-mix(in_srgb,var(--color-link)_8%,white)]" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleEspecialidad(e.codigo)}
                      className="rounded border-slate-300 text-[color:var(--color-link)] focus:ring-[color:var(--color-link)]"
                    />
                    <span className="flex-1 text-slate-700">{e.nombre}</span>
                    {checked && (
                      <button
                        type="button"
                        onClick={() =>
                          setForm({
                            ...form,
                            especialidad_principal: isPrincipal ? null : e.codigo,
                          })
                        }
                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          isPrincipal
                            ? "bg-[color:var(--color-link)] text-white"
                            : "border border-[color:var(--color-link)] text-[color:var(--color-link)] hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
                        }`}
                      >
                        {isPrincipal ? "Principal ✓" : "Marcar principal"}
                      </button>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Sedes */}
        <div className="pt-2 border-t border-slate-100">
          <p className="text-sm font-medium text-slate-700 mb-2">
            Sedes donde atiende
          </p>
          {cargandoSedes ? (
            <p className="text-sm text-slate-500">Cargando sedes…</p>
          ) : errorSedes ? (
            <Alert>
              No se pudieron cargar las sedes. Recarga la página; si sigue,
              avisa al administrador.
            </Alert>
          ) : sedes.length === 0 ? (
            <p className="text-xs text-slate-500">
              Aún no hay sedes registradas en este policlínico.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2">
              {sedes.map((s) => {
                const checked = form.sede_ids.includes(s.id);
                const isPrincipal = form.sede_principal_id === s.id;
                return (
                  <label
                    key={s.id}
                    className={`flex items-center gap-2 text-sm cursor-pointer rounded p-1.5 hover:bg-slate-50 ${
                      checked ? "bg-[color-mix(in_srgb,var(--color-link)_8%,white)]" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSede(s.id)}
                      className="rounded border-slate-300 text-[color:var(--color-link)] focus:ring-[color:var(--color-link)]"
                    />
                    <span className="flex-1 text-slate-700">{s.nombre}</span>
                    {checked && (
                      <button
                        type="button"
                        onClick={() =>
                          setForm({
                            ...form,
                            sede_principal_id: isPrincipal ? null : s.id,
                          })
                        }
                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          isPrincipal
                            ? "bg-[color:var(--color-link)] text-white"
                            : "border border-[color:var(--color-link)] text-[color:var(--color-link)] hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
                        }`}
                      >
                        {isPrincipal ? "Principal ✓" : "Marcar principal"}
                      </button>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            Crear profesional
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
